import { createSlice, createAsyncThunk, type PayloadAction } from "@reduxjs/toolkit";
import * as api from "../service/pricingService";
import type { PricingSection, PricingFeature, PricingTier } from "../types";

interface PricingState {
  section: PricingSection | null;
  loading: boolean;
  saving: boolean;
  error: string | null;
}

const initialState: PricingState = { section: null, loading: false, saving: false, error: null };

export const fetchPricing = createAsyncThunk("cms/pricing/fetch", api.getPricing);
export const savePricingSection = createAsyncThunk(
  "cms/pricing/save",
  (payload: Pick<PricingSection, "cardTitle" | "perStudentCost" | "costLabel" | "renewalHeading" | "gstPercentage">) =>
    api.updatePricingSection(payload)
);
export const addPricingFeature = createAsyncThunk("cms/pricing/addFeature", api.addFeature);
export const removePricingFeature = createAsyncThunk("cms/pricing/removeFeature", api.removeFeature);
export const addPricingTier = createAsyncThunk(
  "cms/pricing/addTier",
  (payload: Omit<PricingTier, "id" | "totalAnnualCost">) => api.addTier(payload)
);
export const editPricingTier = createAsyncThunk(
  "cms/pricing/editTier",
  ({ id, payload }: { id: number; payload: Omit<PricingTier, "id" | "totalAnnualCost"> }) => api.updateTier(id, payload)
);
export const removePricingTier = createAsyncThunk("cms/pricing/removeTier", api.removeTier);

const pricingSlice = createSlice({
  name: "cmsPricing",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchPricing.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(fetchPricing.fulfilled, (state, action: PayloadAction<PricingSection>) => {
        state.loading = false;
        state.section = action.payload;
      })
      .addCase(fetchPricing.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message ?? "Failed to load pricing section.";
      })
      .addCase(savePricingSection.pending, (state) => { state.saving = true; })
      .addCase(savePricingSection.fulfilled, (state, action: PayloadAction<PricingSection>) => {
        state.saving = false;
        state.section = action.payload;
      })
      .addCase(savePricingSection.rejected, (state, action) => {
        state.saving = false;
        state.error = action.error.message ?? "Failed to save section.";
      })
      .addCase(addPricingFeature.fulfilled, (state, action: PayloadAction<PricingFeature>) => {
        state.section?.features.push(action.payload);
      })
      .addCase(removePricingFeature.fulfilled, (state, action: PayloadAction<number>) => {
        if (state.section) {
          state.section.features = state.section.features.filter((f) => f.id !== action.payload);
        }
      })
      .addCase(addPricingTier.fulfilled, (state, action: PayloadAction<PricingTier>) => {
        state.section?.tiers.push(action.payload);
      })
      .addCase(editPricingTier.fulfilled, (state, action: PayloadAction<PricingTier>) => {
        const idx = state.section?.tiers.findIndex((t) => t.id === action.payload.id) ?? -1;
        if (state.section && idx !== -1) state.section.tiers[idx] = action.payload;
      })
      .addCase(removePricingTier.fulfilled, (state, action: PayloadAction<number>) => {
        if (state.section) {
          state.section.tiers = state.section.tiers.filter((t) => t.id !== action.payload);
        }
      });
  },
});

export default pricingSlice.reducer;